import type { Decoration, DecorationSet } from '@atlaskit/editor-prosemirror/view';

import type { ContributorTagModel, DiffDescriptor, TagContributor } from '../../showDiffPluginType';

import type {
	ResolvedDiffContributor,
	ResolvedDiffContributors,
} from './colorSchemes/attributions';
import { type DiffDecorationSpec, isDiffDecoration, isDiffDecorationSpec } from './decorationKeys';

/**
 * Diff kinds that expose an anchor a tag can be pinned to: `inline` is added/changed content,
 * `widget` is deleted content. Block diffs carry no attribution.
 */
const TAGGABLE_DECORATION_TYPES: ReadonlySet<DiffDescriptor['type']> = new Set([
	'inline',
	'widget',
]);

/** The connection is an internal key, so it is dropped on the way to the tag. */
const toTagContributor = ({
	connectedToKey: _connectedToKey,
	...tagContributor
}: ResolvedDiffContributor): TagContributor => tagContributor;

type TaggableDecoration = Decoration & { spec: DiffDecorationSpec };

type TagTarget = {
	contributor: ResolvedDiffContributor;
	decoration: TaggableDecoration;
};

/**
 * Whether a deleted-content widget belongs to the same change as an inline decoration. A replacement
 * renders both — the inserted text inline, the deleted text as a widget anchored to one end of that
 * inline range — and the two must not each claim a tag.
 */
const isSameChange = (widget: TaggableDecoration, inline: TaggableDecoration): boolean =>
	widget.spec.attributionKey === inline.spec.attributionKey &&
	widget.from >= inline.from &&
	widget.from <= inline.to;

/**
 * One model per diff decoration whose attribution resolves to a supplied contributor; anything
 * unattributed, untaggable or unresolved is skipped. A replacement's two decorations are collapsed
 * into the single inline tag — see `isSameChange`.
 */
export const extractContributorTags = (
	decorations: DecorationSet,
	contributors: ResolvedDiffContributors | undefined,
): ContributorTagModel[] => {
	if (!contributors) {
		return [];
	}

	const targets: TagTarget[] = [];

	for (const decoration of decorations.find(undefined, undefined, isDiffDecorationSpec)) {
		if (!isDiffDecoration(decoration)) {
			continue;
		}

		const { spec } = decoration;
		if (!spec.attributionKey || !TAGGABLE_DECORATION_TYPES.has(spec.decorationType)) {
			continue;
		}

		const contributor = contributors[spec.attributionKey];
		if (!contributor) {
			continue;
		}

		targets.push({ contributor, decoration });
	}

	// The inline decoration keeps the tag: it captions content still in the document.
	const inlineTargets = targets.filter(
		({ decoration }) => decoration.spec.decorationType === 'inline',
	);
	const linkedDiffIds = new Map<string, string[]>();
	const folded = new Set<TagTarget>();

	for (const target of targets) {
		if (target.decoration.spec.decorationType !== 'widget') {
			continue;
		}

		const host = inlineTargets.find(({ decoration }) =>
			isSameChange(target.decoration, decoration),
		);
		if (!host) {
			continue;
		}

		folded.add(target);
		const hostDiffId = host.decoration.spec.diffId;
		linkedDiffIds.set(hostDiffId, [
			...(linkedDiffIds.get(hostDiffId) ?? []),
			target.decoration.spec.diffId,
		]);
	}

	return targets
		.filter((target) => !folded.has(target))
		.map(({ contributor, decoration: { spec } }) => {
			const connected = contributor.connectedToKey
				? contributors[contributor.connectedToKey]
				: undefined;
			const connectedContributor = connected ? toTagContributor(connected) : undefined;
			const linked = linkedDiffIds.get(spec.diffId);

			return {
				contributor: toTagContributor(contributor),
				diffId: spec.diffId,
				...(connectedContributor ? { connectedContributor } : {}),
				...(spec.colorScheme ? { colorScheme: spec.colorScheme } : {}),
				...(spec.isActive !== undefined ? { isActive: spec.isActive } : {}),
				...(spec.isInserted !== undefined ? { isInserted: spec.isInserted } : {}),
				...(linked ? { linkedDiffIds: linked } : {}),
			};
		});
};
